import React, { useContext } from 'react';
import { AppContext } from '../../App';
import { 
  TrendingUp, 
  TrendingDown, 
  Package, 
  DollarSign, 
  Clock,
  ArrowUpRight,
  ArrowDownRight,
  Car,
  MapPin
} from 'lucide-react';

const HeroStats = () => {
  const { dashboardData, selectedProvince } = useContext(AppContext);
  
  if (!dashboardData) return null;
  
  const { market_stats, trend_data } = dashboardData;
  
  // Calculate mini sparkline data
  const last7Days = trend_data.slice(-7);
  const inventorySparkline = last7Days.map(d => d.inventory);
  const priceSparkline = last7Days.map(d => d.avg_price);
  
  const MiniSparkline = ({ data, color }) => {
    const min = Math.min(...data);
    const max = Math.max(...data);
    const range = max - min || 1;
    const height = 32;
    const width = 80;
    
    const points = data.map((val, i) => {
      const x = (i / (data.length - 1)) * width;
      const y = height - ((val - min) / range) * height;
      return `${x},${y}`;
    }).join(' ');
    
    return (
      <svg width={width} height={height} className="ml-auto">
        <polyline
          points={points}
          fill="none"
          stroke={color}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  };

  const stats = [
    {
      label: selectedProvince ? `${market_stats.province_name} Inventory` : 'Total Inventory',
      value: market_stats.total_inventory.toLocaleString(),
      change: market_stats.inventory_change_7d,
      icon: Package,
      sparkline: inventorySparkline,
      color: '#3B82F6'
    },
    {
      label: 'Added Today',
      value: market_stats.vehicles_added_today.toLocaleString(),
      change: 12.3,
      icon: TrendingUp,
      color: '#22C55E'
    },
    {
      label: 'Sold Today',
      value: market_stats.vehicles_sold_today.toLocaleString(),
      change: 8.7,
      icon: Car,
      color: '#F97316'
    },
    {
      label: 'Avg Price',
      value: `$${market_stats.avg_price.toLocaleString()}`,
      change: market_stats.price_change_7d,
      icon: DollarSign,
      sparkline: priceSparkline,
      color: '#A855F7'
    },
    {
      label: 'Days on Market',
      value: market_stats.avg_days_on_market.toFixed(1),
      change: -2.4,
      icon: Clock,
      color: '#EC4899'
    }
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="hero-stats">
      {stats.map((stat, index) => {
        const Icon = stat.icon;
        const isPositive = stat.change >= 0;
        
        return (
          <div 
            key={index}
            className="dashboard-card p-5 animate-fade-in"
            style={{ animationDelay: `${index * 0.1}s` }}
            data-testid={`stat-${stat.label.toLowerCase().replace(/\s/g, '-')}`}
          >
            <div className="flex items-start justify-between mb-3">
              <div 
                className="p-2 rounded-lg"
                style={{ backgroundColor: `${stat.color}15` }}
              >
                <Icon className="w-4 h-4" style={{ color: stat.color }} />
              </div>
              <div className={`flex items-center gap-1 text-xs font-medium ${
                isPositive ? 'text-[#22C55E]' : 'text-[#EF4444]'
              }`}>
                {isPositive ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                {Math.abs(stat.change).toFixed(1)}%
              </div>
            </div>
            
            <p className="text-[#A1A1AA] text-xs mb-1">{stat.label}</p>
            
            <div className="flex items-end justify-between">
              <p className="text-2xl font-bold text-white">{stat.value}</p>
              {stat.sparkline && (
                <MiniSparkline data={stat.sparkline} color={stat.color} />
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default HeroStats;
